/**
 * Language Exchange Page Component
 * Alpine.js component for language partner search
 */

import { getCountryCode as getCountryCodeFromMap } from './lang-country-map.js';

// Popular languages to show at top (codes)
const POPULAR_LANGS = ['EN', 'ES', 'FR', 'DE', 'PT', 'IT', 'ZH', 'JA', 'KO', 'RU', 'AR', 'HI'];

// Default proficiency level labels (will be overridden by translations)
const DEFAULT_PROFICIENCY_LABELS = {
    levelAny: 'Any level',
    levelAdvanced: 'Advanced+',
    levelProficient: 'Proficient',
    levelNative: 'Native only'
};

/**
 * Language Exchange component factory
 * @param {Object} config - Configuration object
 * @param {Array} config.speakLangs - Array of language codes the user speaks
 * @param {Array} config.learnLangs - Array of language codes the user wants to learn
 * @param {Object} config.allLangs - Object mapping language codes to names
 */
export function langexComponent(config = {}) {
    const MAX_SPEAK_LANGS = 10;
    const MAX_LEARN_LANGS = 10;

    // Merge translations with defaults
    const labels = { ...DEFAULT_PROFICIENCY_LABELS, ...config.translations };

    // Build proficiency levels with translated labels
    const proficiencyLevels = [
        { value: '', label: labels.levelAny },
        { value: '2', label: labels.levelAdvanced },
        { value: '3', label: labels.levelProficient },
        { value: '4', label: labels.levelNative }
    ];

    return {
        // State
        speakLangs: config.speakLangs || [],
        learnLangs: config.learnLangs || [],
        allLangs: config.allLangs || {},
        minLevel: '2', // Default to Advanced+

        // Picker state
        activePicker: null, // 'speak' or 'learn'
        searchQuery: '',

        // Limits
        maxSpeakLangs: MAX_SPEAK_LANGS,
        maxLearnLangs: MAX_LEARN_LANGS,

        // Constants
        proficiencyLevels: proficiencyLevels,

        // Computed
        get canAddMoreSpeakLangs() {
            return this.speakLangs.length < MAX_SPEAK_LANGS;
        },

        get canAddMoreLearnLangs() {
            return this.learnLangs.length < MAX_LEARN_LANGS;
        },

        get sortedLangs() {
            return Object.entries(this.allLangs)
                .sort(([, a], [, b]) => a.localeCompare(b));
        },

        get popularLangs() {
            return POPULAR_LANGS
                .filter(code => this.allLangs[code])
                .map(code => ({ code, name: this.allLangs[code], countryCode: getCountryCodeFromMap(code) }));
        },

        get filteredLangs() {
            const query = this.searchQuery.toLowerCase().trim();
            if (!query) return [];

            return Object.entries(this.allLangs)
                .map(([code, name]) => ({ code, name, countryCode: getCountryCodeFromMap(code) }))
                .filter(l =>
                    l.name.toLowerCase().includes(query) ||
                    l.code.toLowerCase().includes(query)
                )
                .sort((a, b) => a.name.localeCompare(b.name));
        },

        get allLangsForPicker() {
            return Object.entries(this.allLangs)
                .map(([code, name]) => ({ code, name, countryCode: getCountryCodeFromMap(code) }))
                .sort((a, b) => a.name.localeCompare(b.name));
        },

        get canSubmit() {
            return this.speakLangs.length > 0 || this.learnLangs.length > 0;
        },

        // Methods
        init() {
            // Close picker on escape key
            this.$watch('activePicker', (value) => {
                if (value) {
                    this.$nextTick(() => {
                        this.$refs.pickerSearch?.focus();
                    });
                }
            });
        },

        getCountryCode(code) {
            return getCountryCodeFromMap(code);
        },

        getLangName(code) {
            return this.allLangs[code] || code;
        },

        openPicker(type) {
            this.activePicker = type;
            this.searchQuery = '';
        },

        closePicker() {
            this.activePicker = null;
            this.searchQuery = '';
        },

        isLangSelected(code, type) {
            if (type === 'speak') {
                return this.speakLangs.includes(code);
            }
            return this.learnLangs.includes(code);
        },

        toggleLang(code, type) {
            if (type === 'speak') {
                const index = this.speakLangs.indexOf(code);
                if (index >= 0) {
                    this.speakLangs.splice(index, 1);
                } else if (this.canAddMoreSpeakLangs) {
                    this.speakLangs.push(code);
                }
            } else {
                const index = this.learnLangs.indexOf(code);
                if (index >= 0) {
                    this.learnLangs.splice(index, 1);
                } else if (this.canAddMoreLearnLangs) {
                    this.learnLangs.push(code);
                }
            }
        },

        addLangFromPicker(code) {
            if (this.activePicker === 'speak') {
                if (!this.speakLangs.includes(code) && this.canAddMoreSpeakLangs) {
                    this.speakLangs.push(code);
                }
            } else if (this.activePicker === 'learn') {
                if (!this.learnLangs.includes(code) && this.canAddMoreLearnLangs) {
                    this.learnLangs.push(code);
                }
            }
            this.closePicker();
        },

        removeLang(code, type) {
            if (type === 'speak') {
                const index = this.speakLangs.indexOf(code);
                if (index >= 0) {
                    this.speakLangs.splice(index, 1);
                }
            } else {
                const index = this.learnLangs.indexOf(code);
                if (index >= 0) {
                    this.learnLangs.splice(index, 1);
                }
            }
        },

        handleSubmit() {
            if (!this.canSubmit) {
                return false;
            }
            return true;
        },

        handleClickOutside(event) {
            if (this.activePicker && !event.target.closest('.picker-container')) {
                this.closePicker();
            }
        }
    };
}

// Register with Alpine when it's ready
if (typeof window !== 'undefined') {
    window.langexComponent = langexComponent;
}

export default langexComponent;
